// Libraries
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Modal from 'react-modal';
import { useAuth0 } from '@auth0/auth0-react';
import { FullStory } from '@fullstory/browser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faClose, faBars } from '@fortawesome/free-solid-svg-icons';
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';

// Context
import { UserContext } from '../context/User';
import SessionContext from '../context/Session';

// Components
import Login from './UserComponents/login.js';
import Logout from './UserComponents/logout.js';
import AdminMenu from '../admin/components/AdminHeader.js';
import Icon from './Elements/Icon.js';

// Services
import UserService from '../services/userService';
import utilities from '../services/utilities';

Modal.setAppElement('#root');

const DROPDOWN_KEYS = {
  HELP: 'help',
  FEEDBACK: 'feedback',
  PROFILE: 'profile',
};

// Matches the owner badge in collaborationService.js (coreProcessing) so the
// header icon looks the same as the "owner" bubble shown inside a project.
const OWNER_COLOR = '#7A64EB';

// Mirrors FORMAT_COLLABORATORS in collaborationService.js, which derives a
// collaborator's initials from first_name[0] + last_name[0] -- the logged-in
// `user` object has no precomputed `initials` field of its own.
const getInitials = (user) => {
  const first = user?.first_name ? user.first_name[0] : ' ';
  const last = user?.last_name ? user.last_name[0] : ' ';
  return `${first}${last}`;
};

// Construct Component
const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(UserContext);
  const [isMainDropdownOpen, setIsMainDropdownOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { isAuthenticated, isLoading } = useAuth0();
  const { isMobile, blockNav, setBlockNav, notifyIncompleteWelcome, isViewingPdf, isAccessRequired } =
    useContext(SessionContext);
  const site = window.location.origin;
  // Shared export links (/view/:uuid) are opened by people with no ShotKraft
  // account -- Help/Feedback point at authenticated-only resources, so they
  // don't belong on that page. Uses useLocation (not window.location) so this
  // updates on client-side navigate() calls, not just full page loads.
  const isSharedViewerRoute = location.pathname.startsWith('/view/');

  useEffect(() => {
    let page = window.location.pathname;

    if (site.indexOf('demo') === -1 && site.indexOf('localhost') === -1) {
      if (page === '/') {
        window.location.replace(`https://www.shotkraft.com${page}`);
      } else if (!isLoading && !isAuthenticated && page.indexOf('app/') > 0) {
        // Deep-linking to a page that requires an account (e.g. a shared
        // shotlist URL while logged out) -- show an explanatory page with a
        // login link rather than bouncing straight into the Auth0 redirect.
        // Gated on Auth0's own isAuthenticated rather than the backend
        // `user` context: a brand-new user has no cached `user` in
        // localStorage (see context/User.js), so that context stays empty
        // until the multi-step fullUserDataLoad() chain in UserManager
        // resolves -- which races with Auth0's isLoading flipping to false
        // right after the signup redirect, and was incorrectly bouncing
        // freshly-authenticated new users here.
        navigate('/accessRequired');
      }

      if (page === '/contactUs' || page === '/faq') {
        window.open(`${site}${page}`, '_blank');
      }
    }

    if (page.indexOf('/newUserWelcome') > 0) {
      setBlockNav(true);
    }
  }, [isLoading, isAuthenticated, navigate, site, setBlockNav]);

  const clickLogo = () => {
    if (user && user.id) {
      window.location.replace(`${site}/app/home`);
    } else {
      window.location.replace(site);
    }
  };

  const handleBlockNav = () => {
    if (blockNav) {
      notifyIncompleteWelcome();
    }
  };

  const handleScriptUpload = () => {
    UserService.addActionLog('New Project ', null, null, 'From Header');
    FullStory('trackEvent', {
      name: 'InitiateNewScriptHeader',
    });

    window.location.replace(`${site}/app/newProject/1`);
  };

  const handleClickEvent = (originalOnClick) => (event) => {
    if (blockNav) {
      event.preventDefault();
      handleBlockNav();
    } else if (originalOnClick) {
      originalOnClick(event);
    }
  };

  const openMenu = (key) => {
    if (!blockNav) {
      setOpenDropdown(key);
    }
  };

  const closeMenu = (key) => {
    setOpenDropdown((current) => (current === key ? null : current));
  };

  const isMenuOpen = (key) => openDropdown === key;

  return (
    <>
      <header className="FullWidth header">
        <img
          src="/Logo2White.png"
          style={{ cursor: 'pointer' }}
          className="logo"
          alt="logo"
          onClick={handleClickEvent(clickLogo)}
        />
        <span style={{ flex: 'auto' }}></span>

        {isAuthenticated ? (
          <FontAwesomeIcon
            icon={faBars}
            className="header_nav_icon barIcon"
            onClick={handleClickEvent(() => setIsMainDropdownOpen(!isMainDropdownOpen))}
          />
        ) : (
          <Login useClass="headerItem signupButton" />
        )}

        <nav className={`header_navigation ${isMainDropdownOpen ? 'open' : ''}`}>
          {!isViewingPdf && !isSharedViewerRoute && !isAccessRequired && (
            <>
              <span
                className="headerItem headerDropdownTrigger"
                onMouseEnter={() => openMenu(DROPDOWN_KEYS.HELP)}
                onMouseLeave={() => closeMenu(DROPDOWN_KEYS.HELP)}
                onClick={handleClickEvent()}
              >
                <span className="headerDropdownAnchor" style={{ marginTop: '5px' }}>
                  <FontAwesomeIcon
                    icon={faCircleQuestion}
                    className="header_nav_icon"
                    style={{
                      color: '#7A64EB',
                      marginTop: '-5px',
                      height: '23px',
                      width: '23px',
                      cursor: 'pointer',
                    }}
                  />
                  &nbsp;&nbsp; Help
                  {isMenuOpen(DROPDOWN_KEYS.HELP) && (
                    <div className="headerDropdownMenu headerDropdownMenuLeft headerDropdownMenuHelp">
                      <a
                        className="dropdownItem"
                        onClick={() => closeMenu(DROPDOWN_KEYS.HELP)}
                        href="https://enormous-gecko-407.notion.site/ShotKraft-Help-Center-06b0c2d61ea3414890016c0c0b021d95"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Help Center
                      </a>
                      <a
                        className="dropdownItem"
                        onClick={() => closeMenu(DROPDOWN_KEYS.HELP)}
                        href="https://www.notion.so/Change-log-31af6423eb8a80adbac2e97b5587dec0"
                        target="_blank"
                        rel="noreferrer"
                      >
                        What Changed
                      </a>
                      <a
                        className="dropdownItem"
                        onClick={() => closeMenu(DROPDOWN_KEYS.HELP)}
                        href="https://www.opinionstage.com/page/d17feee5-7a22-45d6-bf28-7ea97b475add"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Roadmap (Vote on Features)
                      </a>
                    </div>
                  )}
                </span>
              </span>

              <span
                className="headerItem headerDropdownTrigger"
                onMouseEnter={() => openMenu(DROPDOWN_KEYS.FEEDBACK)}
                onMouseLeave={() => closeMenu(DROPDOWN_KEYS.FEEDBACK)}
                onClick={handleClickEvent()}
              >
                <span className="headerDropdownAnchor" style={{ marginTop: '10px' }}>
                  <Icon
                    icon="Hand"
                    color="Purple"
                    customClass="header_nav_icon"
                    customStyle={{
                      marginTop: '-5px',
                      height: '20px',
                      width: '16px',
                      cursor: 'pointer',
                    }}
                  />
                  &nbsp;&nbsp; Feedback
                  {isMenuOpen(DROPDOWN_KEYS.FEEDBACK) && (
                    <div className="headerDropdownMenu headerDropdownMenuLeft headerDropdownMenuFeedback">
                      <a
                        className="dropdownItem"
                        onClick={() => closeMenu(DROPDOWN_KEYS.FEEDBACK)}
                        href="https://www.shotkraft.com/report-a-bug"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Report a Bug
                      </a>
                      <a
                        className="dropdownItem"
                        onClick={() => closeMenu(DROPDOWN_KEYS.FEEDBACK)}
                        href="https://www.shotkraft.com/request-a-feature"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Request Feature
                      </a>
                      <a
                        className="dropdownItem"
                        onClick={() => closeMenu(DROPDOWN_KEYS.FEEDBACK)}
                        href="https://www.shotkraft.com/user-feedback"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Give Paid Feedback
                      </a>
                    </div>
                  )}
                </span>
              </span>
            </>
          )}

          {isAuthenticated ? (
            <>
              <span className="headerItem mobileOnly">
                <FontAwesomeIcon
                  icon={faClose}
                  className="linkText"
                  style={{ float: 'right', color: '#0E0F11', fontSize: '24px', paddingTop: '20px' }}
                  onClick={handleClickEvent(() => setIsMainDropdownOpen(!isMainDropdownOpen))}
                />
              </span>

              <a onClick={handleClickEvent(clickLogo)} className="headerItem">
                <Icon
                  icon="Home"
                  color="Purple"
                  customClass="header_nav_icon"
                  customStyle={{ marginTop: '-10px' }}
                />
                &nbsp;&nbsp; My Projects
              </a>

              <a onClick={handleClickEvent(handleScriptUpload)} className="headerItem">
                <Icon
                  icon="Add"
                  color="Purple"
                  customClass="header_nav_icon"
                  customStyle={{ marginTop: '-10px' }}
                />
                &nbsp;&nbsp; New Project
              </a>

              <a href="/app/profile" className="headerItem mobileOnly">
                <FontAwesomeIcon
                  icon={faUser}
                  style={{ color: '#7A64EB', marginBottom: '0px' }}
                  className="header_nav_icon"
                />
                &nbsp;&nbsp; My Profile
              </a>

              {isMainDropdownOpen && user.isStaff && isMobile ? <AdminMenu /> : null}

              <span className="headerItem mobileOnly">
                <FontAwesomeIcon icon={faUser} className="linkText userIcon header_nav_icon" />
                &nbsp;&nbsp;
                <Logout useClass="dropdownMenuItem" />
              </span>

              <span
                className="headerItem userIconA nonMobileOnly headerDropdownTrigger"
                onMouseEnter={() => openMenu(DROPDOWN_KEYS.PROFILE)}
                onMouseLeave={() => closeMenu(DROPDOWN_KEYS.PROFILE)}
                onClick={handleClickEvent()}
              >
                <span className="headerDropdownAnchor headerProfileAnchor">
                  <span
                    className="circleIcon userCircle headerUserCircle"
                    style={{
                      backgroundColor: OWNER_COLOR,
                      color: utilities.isDarkColor(OWNER_COLOR) ? 'white' : 'black',
                    }}
                  >
                    {getInitials(user)}
                  </span>

                  {isMenuOpen(DROPDOWN_KEYS.PROFILE) && (
                    <div className="headerDropdownMenu headerDropdownMenuRight headerDropdownMenuProfile">
                      {user.isStaff ? <AdminMenu /> : null}
                      <a className="dropdownItem" href="/app/profile">
                        My Profile
                      </a>
                      <Logout useClass="dropdownItem" />
                    </div>
                  )}
                </span>
              </span>
            </>
          ) : (
            <></>
          )}
        </nav>
      </header>
    </>
  );
};

export default Header;
